import { useEffect, useState } from "react";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AccountBoxRoundedIcon from "@mui/icons-material/AccountBoxRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import NumbersRoundedIcon from "@mui/icons-material/NumbersRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import {
    Alert,
    Badge,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Snackbar,
    Tab,
    Tabs,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import AccountingBillDeallocatePopup from "@/actuals/AccountingBillDeallocatePopup";
import AccountingBillRefutalConfirmationPopup from "@/actuals/AccountingBillRefutalConfirmationPopup";
import AccountingBillRefutePopup from "@/actuals/AccountingBillRefutePopup";
import AccountingBills, {
    AccountingBillsHeader,
} from "@/actuals/AccountingBills";
import { ActualLinePopup } from "@/actuals/ActualLinePopup";
import { ActualLinesWithHeader } from "@/actuals/ActualLines";
import ActualLinesPopup from "@/actuals/ActualLinesPopup";
import {
    deletePurchaseOrder,
    selectPurchaseOrders,
    selectTab,
    setAccountingAccounts,
    setTaxCodes,
    switchTab,
    updateBills,
    updateLines,
    updatePurchaseOrder,
    updatePurchaseOrders,
    updateTotals,
    resetAll,
    updateProjectDocket,
} from "@/actuals/actualSlice";
import ActualTotals from "@/actuals/ActualTotals";
import AllocationPopup from "@/actuals/AllocationPopup";
import {
    ACCOUNTING_BILL_STATUS_REFUTE_HST,
    ACCOUNTING_BILL_STATUS_REFUTE_OTHER,
    ACCOUNTING_BILL_STATUS_REFUTE_VENDOR,
} from "@/actuals/constants";
import { request } from "@/Api";
import { InternalLink, PageTitle } from "@/common/CommonComponents";
import CsvExportButton from "@/common/CsvExportButton";
import { Heading } from "@/common/typography";
import { ProfileListItem } from "@/profiles_common/CommonComponents";
import ProjectFlow from "@/project_common/ProjectFlow";
import { PROJECT_STATUS_IN_PRODUCTION } from "@/project/constants";
import { getCategories, getTaxCodes } from "./services";
import PurchaseOrders from "@/purchase_orders/PurchaseOrders";

export default function Actuals() {
    const dispatch = useDispatch();

    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [statuses, setStatuses] = useState([]);

    const refuted = (billLine) =>
        billLine.status == ACCOUNTING_BILL_STATUS_REFUTE_HST ||
        billLine.status == ACCOUNTING_BILL_STATUS_REFUTE_OTHER ||
        billLine.status == ACCOUNTING_BILL_STATUS_REFUTE_VENDOR;
    const refutedBillLines = project
        ? project.accounting_bill_lines.filter((billLine) => refuted(billLine))
        : [];
    const unmatchedBillLines = project
        ? project.accounting_bill_lines.filter(
              (billLine) =>
                  billLine.accounting_bill_line_actual_lines.length == 0 &&
                  !refuted(billLine),
          )
        : [];
    const matchedBillLines = project
        ? project.accounting_bill_lines.filter(
              (billLine) =>
                  billLine.accounting_bill_line_actual_lines.length > 0,
          )
        : [];

    const purchaseOrders = Object.values(useSelector(selectPurchaseOrders));

    async function getProject() {
        request.get(`/projects/${projectId}/actuals`).then((response) => {
            const projectData = response.data;
            setProject(projectData);
            dispatch(updateTotals(projectData));
            dispatch(updateLines(projectData.actual_lines));
            dispatch(updateBills(projectData.accounting_bill_lines));
            dispatch(updatePurchaseOrders(projectData.purchase_orders));
            dispatch(updateProjectDocket(projectData.docket));
        });
    }

    const saveProject = (data) => {
        request.patch(`projects/${project.id}`, data).then((response) => {
            const newStatus = response.data.status;
            setProject({
                ...project,
                status: newStatus,
                status_id: newStatus.id,
            });
        });
    };

    async function getStatueses() {
        request.get("statuses").then((response) => {
            setStatuses(
                response.data.reduce(
                    (obj, status) => ((obj[status.name] = status), obj),
                    {},
                ),
            );
        });
    }

    useEffect(() => {
        dispatch(resetAll());
        getProject();
        getStatueses();
        async function loadData() {
            const categories = await getCategories();
            const taxCodes = await getTaxCodes();
            dispatch(setTaxCodes(taxCodes));
            dispatch(setAccountingAccounts(categories));
        }
        loadData();
    }, []);

    if (!project) {
        return (
            <div style={{ position: "relative", height: "100%" }}>
                <CircularProgress
                    sx={{ position: "absolute", top: "50%", left: "50%" }}
                />
            </div>
        );
    }

    const customLines = project.actual_lines
        .filter((actual_line) => !actual_line.line_item_id)
        .sort((a, b) => {
            a.id - b.id;
        });

    const customLinesGroup = {
        isCustomLineGroup: true,
        children: [],
        name: "Custom Lines",
        external_budget: 0, // required
        running: customLines.reduce(
            (sum, actual_line) => sum + actual_line.running,
            0,
        ),
        actual: customLines.reduce(
            (sum, actual_line) => sum + actual_line.actual_project,
            0,
        ),
        line_items: customLines.map((actual_line) => {
            return { actual_line: actual_line };
        }),
    };

    const onStatusUpdate = (statusId) => {
        saveProject({ status_id: statusId });
    };

    const onPurchaseOrderUpdate = (purchaseOrder) => {
        dispatch(updatePurchaseOrder({ ...purchaseOrder }));
    };

    const onPurchaseOrderInvite = (purchaseOrder) => {
        dispatch(updatePurchaseOrder({ ...purchaseOrder, _reminded: true }));
    };

    const onPurchaseOrderDelete = (purchaseOrder) => {
        dispatch(deletePurchaseOrder(purchaseOrder.id));
    };

    return (
        <div style={{ padding: 20, paddingTop: 50 }}>
            <Helmet>
                <PageTitle title="Actualize {project.name}" />
            </Helmet>

            <Grid
                container
                justifyContent={"space-between"}
                alignItems={"center"}
            >
                <Grid item>
                    <Grid container spacing={2} paddingBottom={2}>
                        <Grid item>
                            <InternalLink
                                href={`/projects/${projectId}`}
                                color="primary"
                            >
                                <Heading>{project.name}</Heading>
                            </InternalLink>
                        </Grid>
                        <Grid item>
                            <Chip label="Actualization" color="warning" />
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        alignItems={"center"}
                        spacing={2}
                        paddingBottom={4}
                    >
                        <ProfileListItem
                            text={project.client.name}
                            icon={<AccountBoxRoundedIcon />}
                        />
                        <ProfileListItem
                            text={project.docket}
                            icon={<NumbersRoundedIcon />}
                        />
                        <ProfileListItem
                            text={project.currency}
                            icon={<AccountBalanceWalletRoundedIcon />}
                        />
                        <ProfileListItem
                            text={project.legal_entity.name}
                            icon={<BusinessRoundedIcon />}
                        />
                        <ProfileListItem
                            text={project.owner.name}
                            icon={<PersonRoundedIcon />}
                        />
                        <ProfileListItem
                            text={project.status.name}
                            icon={<AccessTimeRoundedIcon />}
                        />
                    </Grid>
                </Grid>
                <Grid item>
                    <ProjectFlow
                        onStatusUpdate={onStatusUpdate}
                        statuses={statuses}
                        project={project}
                        mode={"actuals"}
                    />
                </Grid>
            </Grid>

            <div style={{ marginBottom: 50 }}>
                <ActualTotals />
            </div>

            <TabSwitcher unmatchedBillsLength={unmatchedBillLines.length} />

            <TabWrapper tabId={0}>
                <div style={{ paddingBottom: 20 }}>
                    <AccountingBillsHeader getProject={getProject} />
                    <Paper
                        sx={{
                            padding: 3,
                            marginBottom: 10,
                            boxShadow: "none",
                            overflow: "auto",
                        }}
                    >
                        <Grid container>
                            <Grid item>
                                <h4
                                    style={{
                                        margin: 0,
                                        paddingBottom: 20,
                                        paddingRight: 20,
                                    }}
                                >
                                    Unapproved bills
                                </h4>
                            </Grid>
                            <Grid item>
                                <Badge
                                    badgeContent={unmatchedBillLines.length}
                                    color="primary"
                                />
                            </Grid>
                        </Grid>
                        <AccountingBills
                            bills={unmatchedBillLines}
                            selectable={true}
                        />
                    </Paper>
                    <div style={{ paddingBottom: 80, overflow: "auto" }}>
                        <Grid item>
                            <h4
                                style={{
                                    margin: 0,
                                    paddingBottom: 20,
                                    paddingRight: 20,
                                }}
                            >
                                Approved bills
                            </h4>
                        </Grid>
                        <AccountingBills
                            bills={matchedBillLines}
                            selectable={false}
                        />
                    </div>
                    <div style={{ overflow: "auto" }}>
                        <Grid item>
                            <h4
                                style={{
                                    margin: 0,
                                    paddingBottom: 20,
                                    paddingRight: 20,
                                }}
                            >
                                Refuted Bills
                            </h4>
                        </Grid>
                        <AccountingBills
                            bills={refutedBillLines}
                            selectable={false}
                        />
                    </div>
                </div>
                {/* TODO: Re-enable zip export */}
                {/* <ExportButton model="accounting-bill" type="zip" filters={{project_id: projectId}} /> */}
            </TabWrapper>

            <TabWrapper tabId={1}>
                <div style={{ paddingBottom: 10 }}>
                    <ActualLinesWithHeader
                        bids={project.confirmed_bids}
                        customLinesGroup={customLinesGroup}
                        selectable={false}
                        getProject={getProject}
                    />
                </div>
                <CsvExportButton
                    model="actual-line"
                    filters={{ project_id: projectId }}
                />
            </TabWrapper>

            <TabWrapper tabId={2}>
                <div style={{ paddingBottom: 10 }}>
                    <PurchaseOrders
                        variant={"actuals"}
                        editable={
                            project.status.name == PROJECT_STATUS_IN_PRODUCTION
                        }
                        purchaseOrders={purchaseOrders}
                        onAdd={onPurchaseOrderUpdate}
                        onUpdate={onPurchaseOrderUpdate}
                        onDelete={onPurchaseOrderDelete}
                        onInvite={onPurchaseOrderInvite}
                    />
                </div>
            </TabWrapper>

            <ActualLinesPopup
                bids={project.confirmed_bids}
                customLinesGroup={customLinesGroup}
            />
            <AllocationPopup
                getProject={getProject}
                openSnackBar={() => {
                    setSnackBarOpen(true);
                }}
            />
            <ActualLinePopup projectId={projectId} getProject={getProject} />
            <AccountingBillDeallocatePopup getProject={getProject} />
            <AccountingBillRefutePopup getProject={getProject} />
            <AccountingBillRefutalConfirmationPopup getProject={getProject} />

            <Snackbar
                open={snackBarOpen}
                autoHideDuration={2000}
                onClose={() => {
                    setSnackBarOpen(false);
                }}
            >
                <Alert severity="success">Bill allocation success!</Alert>
            </Snackbar>
        </div>
    );
}

function TabWrapper({ children, tabId }) {
    const tab = useSelector(selectTab);
    return (
        <div hidden={tabId != tab} style={{ position: "relative" }}>
            {children}
        </div>
    );
}

function TabSwitcher() {
    const dispatch = useDispatch();
    const tab = useSelector(selectTab);
    return (
        <Tabs
            value={tab}
            onChange={(e, tab) => {
                dispatch(switchTab(tab));
            }}
            sx={{
                borderBottom: 1,
                color: "grey",
                marginBottom: 3,
                minHeight: 0,
                padding: 0,
            }}
        >
            <Tab
                label="Bills"
                sx={{
                    fontSize: 12,
                    minWidth: 20,
                    minHeight: 0,
                    paddingLeft: 2,
                    paddingRight: 2,
                }}
            />
            <Tab
                label="Bids"
                sx={{ fontSize: 12, minWidth: 20, minHeight: 0, padding: 1 }}
            />
            <Tab
                label="Purchase Orders"
                sx={{ fontSize: 12, minWidth: 20, minHeight: 0, padding: 1 }}
            />
        </Tabs>
    );
}
